import { AlertTriangle, BarChart3, Shield, ShieldAlert } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/common/ui/card";

import { formatPolicyName } from "./format";
import type { GuardrailMetricsSummary } from "./guardrail-metrics-types";

interface SummaryCardsProps {
  summary: GuardrailMetricsSummary;
}

function rateStatus(rate: number): { label: string; className: string } {
  if (rate <= 2) {
    return { label: "Healthy", className: "text-green-600" };
  }
  if (rate <= 10) {
    return { label: "Elevated", className: "text-yellow-600" };
  }
  return { label: "Critical", className: "text-red-600" };
}

function findTopPolicy(
  policies: GuardrailMetricsSummary["by_policy"]
): { policy: string; count: number } | null {
  if (policies.length === 0) return null;
  let top = policies[0]!;
  for (const p of policies) {
    if (p.count > top.count) {
      top = p;
    }
  }
  return top;
}

export function SummaryCards({ summary }: SummaryCardsProps): React.ReactElement {
  const status = rateStatus(summary.intervention_rate);
  const topPolicy = findTopPolicy(summary.by_policy);

  return (
    <div
      data-testid="summary-cards"
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
    >
      <Card data-testid="total-events-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Events</CardTitle>
          <BarChart3 className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{summary.total_events.toLocaleString()}</div>
        </CardContent>
      </Card>

      <Card data-testid="intervention-count-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Interventions</CardTitle>
          <ShieldAlert className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{summary.intervention_count.toLocaleString()}</div>
        </CardContent>
      </Card>

      <Card data-testid="intervention-rate-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Intervention Rate</CardTitle>
          <Shield className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{summary.intervention_rate.toFixed(1)}%</div>
          <p data-testid="rate-status" className={`text-xs ${status.className}`}>
            {status.label}
          </p>
        </CardContent>
      </Card>

      <Card data-testid="top-policy-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Top Policy</CardTitle>
          <AlertTriangle className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="truncate text-2xl font-bold">
            {topPolicy ? formatPolicyName(topPolicy.policy) : "None"}
          </div>
          {topPolicy && (
            <p className="text-xs text-muted-foreground">{topPolicy.count} interventions</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
